import { useState, useEffect } from 'react'
import {
  Copy,
  Link as LinkIcon,
  Download,
  FileText,
  Image as ImageIcon,
  Video,
  Loader2,
  Inbox,
} from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import referralService from '@/services/referralService'
import partnerService from '@/services/partnerService'
import toast from 'react-hot-toast'
import PageContainer from '@/components/common/PageContainer'
import PageHeader from '@/components/common/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'

const utmSources = [
  { value: 'website', label: 'Sitio Web' },
  { value: 'email', label: 'Email' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'twitter', label: 'Twitter' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'referral', label: 'Referencia personal' },
  { value: 'event', label: 'Evento' },
]

const utmMediums = [
  { value: 'banner', label: 'Banner' },
  { value: 'post', label: 'Post' },
  { value: 'email', label: 'Email' },
  { value: 'cpc', label: 'CPC' },
  { value: 'organic', label: 'Orgánico' },
]

const resourceIcon = (type) => {
  switch (type) {
    case 'image':
      return <ImageIcon size={18} />
    case 'video':
      return <Video size={18} />
    case 'link':
      return <LinkIcon size={18} />
    default:
      return <FileText size={18} />
  }
}

export default function Resources() {
  const { partner } = useAuthStore()
  const [linkParams, setLinkParams] = useState({ source: '', medium: '', campaign: '' })
  const [generatedLink, setGeneratedLink] = useState('')
  const [resources, setResources] = useState([])
  const [loading, setLoading] = useState(true)

  const referralCode =
    typeof partner?.referralCode === 'string'
      ? partner.referralCode
      : partner?.referralCode?.code || 'XXXXXX'

  useEffect(() => {
    (async () => {
      try {
        const response = await partnerService.getResources({
          country: partner?.organization?.country,
        })
        setResources(response.data || [])
      } catch (err) {
        toast.error(err.response?.data?.message || 'Error al cargar materiales')
      } finally {
        setLoading(false)
      }
    })()
  }, [partner])

  const baseLink = referralService.generateReferralLink(referralCode)

  const handleCopy = async (text, label = 'Copiado') => {
    const ok = await referralService.copyToClipboard(text)
    if (ok) toast.success(label)
  }

  const handleGenerate = () => {
    setGeneratedLink(referralService.generateReferralLink(referralCode, linkParams))
  }

  const handleDownload = (url) => {
    toast.success('Descarga iniciada')
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  return (
    <PageContainer>
      <PageHeader
        title="Recursos"
        description="Materiales de marketing y herramientas para partners"
      />

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-h3">Tu código de referido</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3">
              <code className="font-mono font-semibold text-h2 text-primary tracking-wider px-4 py-2 rounded-md bg-primary-50 dark:bg-primary-900/40 border border-primary-100 dark:border-primary-800">
                {referralCode}
              </code>
              <Button
                variant="outline"
                size="icon"
                onClick={() => handleCopy(referralCode, 'Código copiado')}
                aria-label="Copiar código"
              >
                <Copy size={16} />
              </Button>
            </div>
            <p className="text-body-sm text-subtle">
              Comparte este código. Lo usarán al registrarse para que recibas comisiones.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-h3">Link de referido básico</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="relative">
              <Input value={baseLink} readOnly className="pr-12 font-mono text-body-sm" />
              <button
                type="button"
                onClick={() => handleCopy(baseLink, 'Link copiado')}
                className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-10 w-10 items-center justify-center rounded-md text-primary hover:bg-primary-50 dark:hover:bg-primary-900/40"
                aria-label="Copiar"
              >
                <Copy size={16} />
              </button>
            </div>
            <p className="text-body-sm text-subtle">
              Cualquier registro a través de este enlace se asocia a tu cuenta.
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-h3">Generador de links con tracking</CardTitle>
          <p className="text-body-sm text-subtle mt-1">
            Genera links personalizados con parámetros UTM para rastrear el origen.
          </p>
        </CardHeader>
        <CardContent>
          <Separator className="mb-4" />
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>Fuente (utm_source)</Label>
              <Select
                value={linkParams.source || 'none'}
                onValueChange={(v) =>
                  setLinkParams((p) => ({ ...p, source: v === 'none' ? '' : v }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Ninguna —</SelectItem>
                  {utmSources.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Medio (utm_medium)</Label>
              <Select
                value={linkParams.medium || 'none'}
                onValueChange={(v) =>
                  setLinkParams((p) => ({ ...p, medium: v === 'none' ? '' : v }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Ninguno —</SelectItem>
                  {utmMediums.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="utm-campaign">Campaña (utm_campaign)</Label>
              <Input
                id="utm-campaign"
                value={linkParams.campaign}
                onChange={(e) =>
                  setLinkParams((p) => ({ ...p, campaign: e.target.value }))
                }
                placeholder="mi-campaña-2026"
              />
            </div>
          </div>

          <Button onClick={handleGenerate} className="mt-4">
            <LinkIcon size={16} /> Generar link
          </Button>

          {generatedLink && (
            <div className="mt-4 space-y-1.5">
              <Label>Link generado</Label>
              <div className="relative">
                <Input
                  value={generatedLink}
                  readOnly
                  className="pr-12 font-mono text-body-sm"
                />
                <button
                  type="button"
                  onClick={() => handleCopy(generatedLink, 'Link copiado')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-10 w-10 items-center justify-center rounded-md text-primary hover:bg-primary-50 dark:hover:bg-primary-900/40"
                  aria-label="Copiar"
                >
                  <Copy size={16} />
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-h3">Materiales de marketing</CardTitle>
          <p className="text-body-sm text-subtle mt-1">
            Descarga recursos para promocionar CoreDesk
          </p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-6">
              <Loader2 size={24} className="animate-spin text-primary" />
            </div>
          ) : resources.length === 0 ? (
            <div className="flex flex-col items-center py-8 text-center">
              <Inbox size={32} className="text-neutral-400 dark:text-neutral-500 mb-2" />
              <p className="text-body-sm text-subtle">
                No hay materiales disponibles actualmente.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-line">
              {resources.map((r) => (
                <li
                  key={r._id}
                  className="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-primary-50 dark:bg-primary-900/40 text-primary shrink-0">
                    {resourceIcon(r.type)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-body font-medium text-foreground truncate">
                      {r.title}
                    </p>
                    {r.description && (
                      <p className="text-caption text-subtle truncate">
                        {r.description}
                      </p>
                    )}
                  </div>
                  <Badge variant="outline" className="shrink-0 hidden sm:inline-flex">
                    {(r.type || '').toUpperCase()}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDownload(r.url)}
                    aria-label="Descargar"
                  >
                    <Download size={16} />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  )
}
